import React from 'react';
import type { AlbumExport, ExportState } from '../../hooks/useAlbumExport';
import { ChooseView, LockedView } from './AlbumExportChoose';
import { PRIMARY, SECONDARY, plural } from './dialogParts';

type Done = Extract<ExportState, { status: 'done' }>;

function DoneView({ exp, state }: { exp: AlbumExport; state: Done }) {
  const { saved, skipped, failed, outcome, fatalMessage } = state.result;
  // A stopped ZIP run discards the part it was building, so its counts are not files on disk.
  const stoppedZip = state.mode === 'zip' && outcome !== 'complete';
  const summary = [
    `${saved.length} saved`,
    skipped.length > 0 && `${skipped.length} already there`,
    failed.length > 0 && `${failed.length} failed`,
  ].filter(Boolean).join(' · ');
  return (
    <div className="space-y-3">
      <p className="text-gray-900 font-medium">{outcome === 'cancelled' ? 'Export cancelled' : outcome === 'fatal' ? 'Export stopped' : 'Export finished'}</p>
      <p className="text-gray-700">{stoppedZip ? 'Only ZIP files that had already finished downloading were kept.' : summary}</p>
      {fatalMessage && <p className="text-sm text-red-600">{fatalMessage}</p>}
      {state.mode === 'zip' && outcome === 'complete' && state.zipFiles > 0 && (
        <p className="text-sm text-gray-500">
          Check your downloads folder for {plural(state.zipFiles, 'ZIP file')}. If your browser asked to allow multiple downloads and one is missing, export again.
        </p>
      )}
      {failed.length > 0 && !stoppedZip && <button type="button" className={PRIMARY} onClick={exp.retryFailed}>Retry failed</button>}
      <button type="button" className={SECONDARY} onClick={exp.close}>Close</button>
    </div>
  );
}

function ProgressView({ exp, done, total }: { exp: AlbumExport; done: number; total: number }) {
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="space-y-3">
      <p className="text-gray-700">{total > 0 ? `${done} of ${total}` : 'Getting ready…'}</p>
      <progress className="w-full h-2" value={percent} max={100} />
      <p className="text-sm text-gray-500">Keep this tab open until the export finishes.</p>
      <button type="button" className={SECONDARY} onClick={exp.cancel}>Cancel</button>
    </div>
  );
}

interface AlbumExportDialogProps {
  exp: AlbumExport;
  albumTitle: string;
  /** Opens the access-request flow. Omit when the viewer cannot request (not signed in). */
  onRequestAccess?: () => void;
}

export function AlbumExportDialog({ exp, albumTitle, onRequestAccess }: AlbumExportDialogProps) {
  const { state } = exp;
  if (state.status === 'idle') return null;
  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="Export album">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 text-left shadow-xl">
        <h2 className="mb-4 text-lg font-bold text-gray-900 truncate">Export “{albumTitle}”</h2>
        {state.status === 'locked' && <LockedView exp={exp} onRequestAccess={onRequestAccess} />}
        {state.status === 'choosing' && <ChooseView exp={exp} state={state} />}
        {state.status === 'preparing' && <ProgressView exp={exp} done={0} total={0} />}
        {state.status === 'running' && <ProgressView exp={exp} done={state.progress.done} total={state.progress.total} />}
        {state.status === 'done' && <DoneView exp={exp} state={state} />}
        {state.status === 'error' && (
          <div className="space-y-3">
            <p className="text-red-600">{state.message}</p>
            <button type="button" className={SECONDARY} onClick={exp.close}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
